import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Radio, Tag } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { useP1304Query } from './hooks/useP1304Query';
import type { CompanyRow, CodeMapRow } from '../../services/p1304Api';
import './P1304Form.css';

const P1304Form: React.FC = () => {
  const { loading, companies, codeMap, groups, queried, noData, runQuery, reset } = useP1304Query();

  const [reportType, setReportType] = useState<string>('company');
  const [categoryType, setCategoryType] = useState<string>('all');
  const [companyFrom, setCompanyFrom] = useState('');
  const [companyTo, setCompanyTo] = useState('');

  const handleQuery = () => {
    runQuery({
      report_type: reportType,
      category_type: categoryType || undefined,
      company_from: companyFrom.trim() || undefined,
      company_to: companyTo.trim() || undefined,
    });
  };

  const handleReset = () => {
    setReportType('company');
    setCategoryType('all');
    setCompanyFrom('');
    setCompanyTo('');
    reset();
  };

  const handlePrint = () => {
    window.print();
  };

  const companyColumns: ColumnsType<CompanyRow> = [
    { title: '公司代號', dataIndex: 'F104_01', key: 'F104_01', width: 100 },
    { title: '類別', dataIndex: 'F104_02', key: 'F104_02', width: 60 },
    { title: '簡稱', dataIndex: 'F104_11', key: 'F104_11', width: 100 },
    { title: '中文名稱', dataIndex: 'F104_12', key: 'F104_12', ellipsis: true, width: 200 },
    { title: '英文名稱', dataIndex: 'F104_13', key: 'F104_13', ellipsis: true, width: 180 },
    { title: '幣別', dataIndex: 'F104_14', key: 'F104_14', width: 60 },
    { title: '統一編號', dataIndex: 'F104_17', key: 'F104_17', width: 100 },
    { title: '電話', dataIndex: 'F104_21', key: 'F104_21', width: 120 },
    {
      title: '地址', key: 'addresses', width: 200, ellipsis: true,
      render: (_: unknown, r: CompanyRow) =>
        r.addresses?.length > 0
          ? r.addresses.map((a, i) => <div key={i}>{a.F1041_11}: {a.F1041_12}</div>)
          : '-',
    },
    {
      title: '聯絡人', key: 'contacts', width: 200, ellipsis: true,
      render: (_: unknown, r: CompanyRow) =>
        r.contacts?.length > 0
          ? r.contacts.map((c, i) => <div key={i}>{c.F1042_02} ({c.F1042_11})</div>)
          : '-',
    },
  ];

  const codeMapColumns: ColumnsType<CodeMapRow> = [
    { title: '公司代號', dataIndex: 'F104_01', key: 'F104_01', width: 120 },
    { title: '類別', dataIndex: 'F104_02', key: 'F104_02', width: 60 },
    { title: '簡稱', dataIndex: 'F104_11', key: 'F104_11', width: 120 },
    { title: '中文名稱', dataIndex: 'F104_12', key: 'F104_12', ellipsis: true },
    { title: '英文名稱', dataIndex: 'F104_13', key: 'F104_13', ellipsis: true },
  ];

  const filterLabel = (text: string) => (
    <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{text}</label>
  );

  return (
    <PermissionGuard module={Modules.P1304} action="query">
      <div className="p1304-container">
        <div className="p1304-header">
          <h2>公司資料列印</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 12 }}>
            {filterLabel('報表類型')}
            <Radio.Group value={reportType} onChange={e => setReportType(e.target.value)}>
              <Radio.Button value="company">公司資料清冊</Radio.Button>
              <Radio.Button value="codemap">新舊代號對照表</Radio.Button>
              <Radio.Button value="both">兩者都印</Radio.Button>
            </Radio.Group>
          </div>
          <div style={{ marginBottom: 12 }}>
            {filterLabel('公司類別')}
            <Radio.Group value={categoryType} onChange={e => setCategoryType(e.target.value)}>
              <Radio.Button value="all">全部</Radio.Button>
              <Radio.Button value="C">C 類</Radio.Button>
              <Radio.Button value="CD">CD 至 D</Radio.Button>
              <Radio.Button value="F">F 類</Radio.Button>
              <Radio.Button value="M">M 類</Radio.Button>
            </Radio.Group>
          </div>
          <Space wrap size="middle">
            <div>
              {filterLabel('公司代號起')}
              <Input value={companyFrom} onChange={e => setCompanyFrom(e.target.value)} placeholder="起" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('公司代號迄')}
              <Input value={companyTo} onChange={e => setCompanyTo(e.target.value)} placeholder="迄" style={{ width: 140 }} allowClear />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <>
            {groups && groups.length > 0 && (
              <div style={{ marginBottom: 12, fontSize: 13, color: '#555' }}>
                類別筆數：{groups.map(g => <Tag key={g.code}>{g.code || '(空)'}({g.count})</Tag>)}
              </div>
            )}
            {companies.length > 0 && (
              <>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>公司資料清冊</div>
                <Table<CompanyRow>
                  columns={companyColumns}
                  dataSource={companies}
                  rowKey={(r) => r.F104_01}
                  loading={loading}
                  size="small"
                  pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
                  scroll={{ x: 1300 }}
                  style={{ marginBottom: 16 }}
                />
              </>
            )}
            {codeMap.length > 0 && (
              <>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>新舊代號對照表</div>
                <Table<CodeMapRow>
                  columns={codeMapColumns}
                  dataSource={codeMap}
                  rowKey={(r) => r.F104_01}
                  loading={loading}
                  size="small"
                  pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
                  scroll={{ x: 700 }}
                />
              </>
            )}
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default P1304Form;
